import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Spinner } from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { ProductSalesCard } from './ProductSalesCard'
import type { ProductSales } from './types'

/**
 * This month's product sales, from the same endpoint and the same numbers as
 * the statistics page. Other periods and comparisons live there.
 */
export function SalesTab() {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const { slug } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; sales: ProductSales | null; currency: string }
  >({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const now = new Date()
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/gym-stats?mode=fixed&unit=month&a=${month}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        if (!res.ok) {
          setState({ status: 'error' })
          return
        }
        const stats = (await res.json()) as { productSales: ProductSales | null; currency: string }
        if (!cancelled) {
          setState({ status: 'ready', sales: stats.productSales, currency: stats.currency })
        }
      } catch {
        if (!cancelled) setState({ status: 'error' })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently])

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }
  if (state.status === 'error' || !state.sales) {
    return <p className="text-sm text-danger">{t('products.errorLoad')}</p>
  }

  return (
    <div className="max-w-xl space-y-3">
      <ProductSalesCard
        sales={state.sales}
        currency={state.currency}
        periodLabel={t('products.salesThisMonth')}
      />
      <Link to={`/${slug}/statistics`} className="text-sm text-primary hover:underline">
        {t('products.seeStatistics')} →
      </Link>
    </div>
  )
}
